import {envIsDev} from '../../../config';
import {REGISTERED_DURATION_CONTRACTS_BY_NAME} from './durationContracts';
import {REGISTERED_DURATION_CONTRACT_ENTRIES} from './marks';
import type {PerformanceDurationContract} from './types';

const durationMarkFields = ['startMark', 'endMark'] as const;

/**
 * Checks the static allowlist in dev before marks can pair
 */
export function validateDurationContracts(): void {
  // no need to do validation unless we're in developing locally
  // it's safe to throw errors there and we can avoid an explicit lint rule with this
  if (!envIsDev) {
    return;
  }

  const validatedContracts: PerformanceDurationContract[] = [];
  const contractsByMarkName = new Map<string, PerformanceDurationContract>();

  for (const [markName, {contract}] of REGISTERED_DURATION_CONTRACT_ENTRIES) {
    const existingContract = contractsByMarkName.get(markName);
    if (existingContract != null && existingContract !== contract) {
      throw new Error(`duplicate performance duration mark name: ${markName}`);
    }
    contractsByMarkName.set(markName, contract);
  }

  for (const {contract} of REGISTERED_DURATION_CONTRACTS_BY_NAME.values()) {
    if (validatedContracts.includes(contract)) {
      continue;
    }

    const errorMessage = validateDurationContract(contract, validatedContracts);

    if (errorMessage != null) {
      throw new Error(errorMessage);
    }

    validatedContracts.push(contract);
  }
}

function validateDurationContract(
  contract: PerformanceDurationContract,
  validContracts: PerformanceDurationContract[]
): string | null {
  if (contract.startMark === contract.endMark) {
    return `performance duration startMark and endMark must differ: ${contract.startMark}`;
  }

  for (const pendingContract of validContracts) {
    if (pendingContract.durationName === contract.durationName) {
      return `duplicate performance duration name: ${contract.durationName}`;
    }

    for (const field of durationMarkFields) {
      const markName = contract[field];
      if (
        markName === pendingContract.startMark ||
        markName === pendingContract.endMark
      ) {
        return `duplicate performance duration ${field}: ${markName}`;
      }
    }
  }

  return null;
}
